import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, trackPageview } from "../lib/api";
import { openSubscribe } from "../components/SubscribeModalHost";
import { Header } from "../components/Header";
import { HeroSection } from "../components/FeaturedHero";
import { HeroCarousel } from "../components/HeroCarousel";
import { HeroCTAStrip } from "../components/HeroCTAStrip";
import { SpotlightSection } from "../components/SpotlightSection";
import { IslandEssentials } from "../components/IslandEssentials";
import { ArticleCard } from "../components/ArticleCard";
import { ContinueReading } from "../components/ContinueReading";
import { TrendingTopics } from "../components/TrendingTopics";
import { WhatsAppCTA, SourceTiles } from "../components/Sidebar";
import { Footer } from "../components/Footer";
import { WeatherWidget } from "../components/WeatherWidget";
import { MostReadWidget } from "../components/MostReadWidget";
import { PushOptIn } from "../components/PushOptIn";
import {
  LeaderboardAd,
  RectangleAd,
  SidebarTallAd,
  SidebarStickyAd,
  SponsoredCategoryLabel,
} from "../components/AdSlot";
import { ISLAND_BY_SLUG, ISLAND_META_DESC } from "../lib/constants";
import { LangContext } from "../lib/LangContext";
import { t as tFn, tCategory, tIsland } from "../lib/i18n";
import { useSeoTags } from "../lib/useSeoTags";

const ISLAND_KEY = "ir_island";
const LANG_KEY = "ir_lang";
const SITE = "https://islandsdaily.com";

// Localized meta descriptions per island × language.
// "all" = All Islands (home).
const HOME_META = {
  all: {
    en: "IslandsDaily — Dutch Caribbean news in English. Aruba, Curaçao, Bonaire and St. Maarten headlines aggregated daily from leading local sources. Free WhatsApp digest.",
    nl: "IslandsDaily — Nederlands Caribisch nieuws in het Nederlands. Koppen uit Aruba, Curaçao, Bonaire en St. Maarten dagelijks samengevat uit lokale bronnen. Gratis WhatsApp-briefing.",
    es: "IslandsDaily — Noticias del Caribe holandés en español. Titulares de Aruba, Curazao, Bonaire y San Martín resumidos a diario. Resumen por WhatsApp gratis.",
  },
  aruba: {
    en: "All of Aruba's news in English — 24ora, Noticia Cla, AWE24 and Mas Noticia aggregated daily. Free WhatsApp Daily Digest.",
    nl: "Al het Aruba-nieuws in het Nederlands — 24ora, Noticia Cla, AWE24 en Mas Noticia dagelijks samengevat. Gratis WhatsApp Dagelijkse Briefing.",
    es: "Todas las noticias de Aruba en español — 24ora, Noticia Cla, AWE24 y Mas Noticia resumidas a diario. Resumen Diario por WhatsApp gratis.",
  },
  curacao: {
    en: "All of Curaçao's news in English — Nu.cw, Knipselkrant Curaçao and Curaçao Chronicle aggregated daily. Free WhatsApp Daily Digest.",
    nl: "Al het Curaçao-nieuws in het Nederlands — Nu.cw, Knipselkrant Curaçao en Curaçao Chronicle dagelijks samengevat. Gratis WhatsApp-briefing.",
    es: "Todas las noticias de Curazao en español — Nu.cw, Knipselkrant Curaçao y Curaçao Chronicle resumidas a diario. Resumen por WhatsApp gratis.",
  },
  bonaire: {
    en: "All of Bonaire's news in English — InfoBonaire, Bonaire Island and Bonaire Reporter aggregated daily. Free WhatsApp Daily Digest.",
    nl: "Al het Bonaire-nieuws in het Nederlands — InfoBonaire, Bonaire Island en Bonaire Reporter dagelijks samengevat. Gratis WhatsApp-briefing.",
    es: "Todas las noticias de Bonaire en español — InfoBonaire, Bonaire Island y Bonaire Reporter resumidas a diario. Resumen por WhatsApp gratis.",
  },
  stmaarten: {
    en: "All of St. Maarten's news in English — The Daily Herald, SXM Island Time and 721 News aggregated daily. Free WhatsApp Daily Digest.",
    nl: "Al het St. Maarten-nieuws in het Nederlands — The Daily Herald, SXM Island Time en 721 News dagelijks samengevat. Gratis WhatsApp-briefing.",
    es: "Todas las noticias de San Martín en español — The Daily Herald, SXM Island Time y 721 News resumidas a diario. Resumen por WhatsApp gratis.",
  },
};

// URL slug used in canonical (matches App.js dynamic island routes)
const CANONICAL_SLUG = { stmaarten: "st-maarten" };

const buildHomeSeoConfigs = (island, lang) => {
  const meta = HOME_META[island] || HOME_META.all;
  const canonicalPath =
    island === "all" ? "" : `/${CANONICAL_SLUG[island] || island}`;
  const url = `${SITE}${canonicalPath}`;
  // HomePage language switching is purely client-side, so every language
  // variant lives at the same URL. Emit hreflang tags pointing at that
  // single URL for all three languages + x-default so Google knows the
  // page serves multi-lingual content.
  const alternates = [
    { hreflang: "en", href: url },
    { hreflang: "nl", href: url },
    { hreflang: "es", href: url },
    { hreflang: "x-default", href: url },
  ];
  const build = (lc) => {
    const islandName =
      island === "all"
        ? { en: "All Islands", nl: "Alle eilanden", es: "Todas las islas" }[lc]
        : tIsland(lc, island);
    const brand =
      island === "all"
        ? `IslandsDaily — ${islandName}`
        : `${islandName} — IslandsDaily`;
    return {
      title: brand,
      description: meta[lc] || meta.en,
      canonical: url,
      alternates,
    };
  };
  return { en: build("en"), nl: build("nl"), es: build("es") };
};

// URL slug -> category display name
const CATEGORY_FROM_SLUG = {
  "tourism": "Tourism",
  "business": "Business",
  "crime-safety": "Crime & Safety",
  "politics": "Politics",
  "community": "Community",
  "weather": "Weather",
  "sports": "Sports",
  "entertainment": "Entertainment",
};

export default function HomePage({ initialIsland }) {
  const params = useParams();
  const urlCategory = params.category
    ? CATEGORY_FROM_SLUG[params.category.toLowerCase()] || "All"
    : "All";
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [featuredMulti, setFeaturedMulti] = useState([]);
  const [featuredIslandList, setFeaturedIslandList] = useState([]);
  const [trending, setTrending] = useState(null);
  const [ads, setAds] = useState({});
  const [weather, setWeather] = useState({});
  const [category, setCategory] = useState(urlCategory);
  const [island, setIsland] = useState(() => {
    if (initialIsland && ISLAND_BY_SLUG[initialIsland]) return initialIsland;
    try {
      const stored = localStorage.getItem(ISLAND_KEY);
      if (stored && ISLAND_BY_SLUG[stored]) return stored;
    } catch (e) { /* ignore */ }
    return "all";
  });
  const [lang, setLang] = useState(() => {
    try {
      // Honor ?lang=xx URL query parameter first (highest priority) — allows
      // linking directly to a specific language, and satisfies the i18n test
      // contract from 2026-07-18.
      const SUPPORTED_LANGS = ["en", "es", "nl", "pap", "pap_aw"];
      const params = new URLSearchParams(window.location.search);
      const q = (params.get("lang") || "").toLowerCase();
      if (SUPPORTED_LANGS.includes(q)) return q;
      const stored = localStorage.getItem(LANG_KEY);
      if (stored && SUPPORTED_LANGS.includes(stored)) return stored;
      // First-time visitor: try to honor their browser locale so a Dutch
      // reader lands on Dutch and a Spanish reader lands on Spanish. Falls
      // back to English for everyone else.
      const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
      if (nav.startsWith("nl")) return "nl";
      if (nav.startsWith("es")) return "es";
    } catch (e) { /* ignore */ }
    return "en";
  });
  // Local UI state kept per-mount:
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageview("/");
  }, []);

  // Auto-open the SubscribeModal when landing via ?subscribe=1 — this
  // is the target for the WhatsApp digest "forward-to-a-friend" share
  // loop. Runs once on mount. Removes the param from the URL after
  // opening so a page refresh doesn't re-trigger the modal.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("subscribe") === "1") {
        openSubscribe("digest_forward");
        params.delete("subscribe");
        const q = params.toString();
        window.history.replaceState(
          {}, "",
          window.location.pathname + (q ? `?${q}` : "") + window.location.hash
        );
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Weather: load once and refresh every 15 min while the tab is open
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api
        .get("/weather")
        .then((r) => { if (!cancelled) setWeather(r.data || {}); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 15 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(ISLAND_KEY, island);
    } catch (e) { /* ignore */ }
  }, [island]);

  // Trilingual SEO: title, description, canonical + og:title/description
  // + <html lang> auto-switch per active locale.
  const seoConfigs = useMemo(() => buildHomeSeoConfigs(island, lang), [island, lang]);
  useSeoTags(lang, seoConfigs);

  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, lang); } catch (e) { /* ignore */ }
    // Notify same-tab listeners (FloatingSubscribeCTA, MobileStickyBar) that
    // live outside HomePage's <LangContext.Provider>. Cross-tab syncs come
    // free via the browser's native 'storage' event.
    try { window.dispatchEvent(new Event("ir_lang_changed")); } catch (_) { /* ignore */ }
  }, [lang]);

  // Scroll restoration: persist Y per (island, category, lang) so navigating
  // Back from an article on mobile returns the reader to where they left off.
  const scrollKey = `homepage-scroll:${island}:${category}:${lang}`;
  const isFirstMount = useRef(true);
  const prevSelection = useRef({ island, category });
  // Restore from sessionStorage fires AT MOST ONCE per HomePage mount — only
  // for the "Back from article" case. Any subsequent island / category change
  // skips restore so the user lands at the top of the new feed (per spec).
  const hasAttemptedRestore = useRef(false);

  // Disable browser auto-scroll-restore — we manage it ourselves so it never
  // races with the async articles fetch.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Save scroll Y on every scroll (debounced 200ms) + on pagehide. This
  // persists the latest position so navigating away (article click, tab hide,
  // browser back-forward-cache on mobile) always has a fresh Y in sessionStorage.
  // NOTE: we intentionally do NOT save in the effect cleanup — React StrictMode
  // double-mounts components in dev, and saving on cleanup would overwrite the
  // saved value with the post-mount scrollY (which is 0) before our restore
  // logic runs.
  useEffect(() => {
    let timeout = null;
    const save = () => {
      try {
        sessionStorage.setItem(scrollKey, String(window.scrollY));
      } catch (e) { /* ignore quota / private-mode errors */ }
    };
    const onScroll = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(save, 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", save);
    return () => {
      if (timeout) clearTimeout(timeout);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", save);
    };
  }, [scrollKey]);

  // Scroll-to-top only when the user *actively* switches island or category
  // — never on the initial mount (which happens on Back navigation).
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      prevSelection.current = { island, category };
      return;
    }
    if (
      prevSelection.current.island !== island ||
      prevSelection.current.category !== category
    ) {
      prevSelection.current = { island, category };
      try {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    }
  }, [island, category]);

  useEffect(() => {
    setLoading(true);
    const params = { lang };
    if (category !== "All") params.category = category;
    if (island !== "all") params.island = island;
    const adParams = island !== "all" ? { island } : {};
    const featParams = island !== "all" ? { island, lang } : { lang };
    Promise.all([
      api.get("/articles", { params }).then((r) => setArticles(r.data)),
      api.get("/articles/featured", { params: featParams }).then((r) => setFeatured(r.data)),
      // Homepage hero carousel (only used when island === "all"). Fetched
      // unconditionally so a switch back to "all" doesn't flash empty.
      api.get("/articles/featured-multi", { params: { lang } })
        .then((r) => setFeaturedMulti(Array.isArray(r.data) ? r.data : []))
        .catch(() => setFeaturedMulti([])),
      // Island-specific hero carousel. Only meaningful when a single
      // island is selected — but we fetch it whenever `island !== "all"`
      // so switching islands is instant. When `island === "all"` we skip
      // and let `featuredMulti` drive the hero. `limit=8` matches the
      // `_TOTAL_SLOTS=8` cap the "All Islands" hero uses, so a
      // per-island view isn't visually stunted vs the combined view —
      // reader parity across island filters is the goal (Calvin 2026-02).
      island !== "all"
        ? api.get("/articles/featured-list", { params: { island, lang, limit: 8 } })
            .then((r) => setFeaturedIslandList(Array.isArray(r.data) ? r.data : []))
            .catch(() => setFeaturedIslandList([]))
        : Promise.resolve(setFeaturedIslandList([])),
      api.get("/articles/trending", { params: island !== "all" ? { island } : {} })
        .then((r) => setTrending(r.data)),
      api.get("/ads", { params: adParams }).then((r) => setAds(r.data || {})),
    ])
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        // Restore saved scroll Y once the article DOM has had a paint cycle.
        // ONLY on the very first data load after mount (the "Back from
        // article" case). After that, any island/category change must scroll
        // to top — the scroll-to-top effect handles that.
        if (hasAttemptedRestore.current) return;
        hasAttemptedRestore.current = true;
        try {
          const saved = sessionStorage.getItem(scrollKey);
          if (saved !== null) {
            const y = parseInt(saved, 10);
            if (!isNaN(y) && y > 0) {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  window.scrollTo({ top: y, behavior: "instant" });
                });
              });
            }
          }
        } catch (e) { /* ignore */ }
      });
  }, [category, island, lang, scrollKey]);

  // Exclude anything already shown in the hero from the article grid.
  // In "all islands" mode the carousel shows up to 4 slides (one per
  // island). On island-specific pages the carousel shows that island's
  // top 3-5 headlines. Both cases dedupe against a set of IDs.
  const heroIds = new Set(
    island === "all"
      ? featuredMulti.map((a) => a && a.id).filter(Boolean)
      : (featuredIslandList.length
          ? featuredIslandList.map((a) => a && a.id).filter(Boolean)
          : (featured ? [featured.id] : []))
  );
  const rest =
    category === "All"
      ? articles.filter((a) => !heroIds.has(a.id))
      : articles;

  const islandMeta = ISLAND_BY_SLUG[island];
  const showIslandFlagOnCards = island === "all";

  return (
    <LangContext.Provider value={lang}>
      <div className="min-h-screen bg-white text-[#1A1A1A]">
        <Header
          category={category}
          onCategoryChange={setCategory}
          island={island}
          onIslandChange={setIsland}
          lang={lang}
          onLangChange={setLang}
          onSubscribe={() => openSubscribe("header")}
          weather={weather}
        />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {category === "All" && (
            island === "all"
              ? <HeroCarousel articles={featuredMulti.length ? featuredMulti : (featured ? [featured] : [])} />
              : <HeroCarousel articles={featuredIslandList.length ? featuredIslandList : (featured ? [featured] : [])} />
          )}
          <div className="mb-6" data-testid="hero-cta-strip-wrap">
            <HeroCTAStrip source="hero_strip" testid="hero-cta-strip" />
          </div>

          <SpotlightSection island={island} lang={lang} ads={ads} />

          <IslandEssentials island={island} />

          <div className="mb-8">
            <LeaderboardAd ad={ads.leaderboard} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <ContinueReading />
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <h2
                  className="font-serif-display text-2xl text-[#1A1A1A] flex items-center gap-2 flex-wrap"
                  data-testid="feed-heading"
                >
                  {islandMeta && islandMeta.slug !== "all" && (
                    <span className="text-[22px]">{islandMeta.flag}</span>
                  )}
                  <span>
                    {category === "All"
                      ? islandMeta && islandMeta.slug !== "all"
                        ? `${tIsland(lang, islandMeta.slug)} · ${tFn(lang, "latest")}`
                        : tFn(lang, "latest_across")
                      : tCategory(lang, category)}
                  </span>
                  {ads.sponsored_category && ads.sponsored_category[category] && (
                    <SponsoredCategoryLabel ad={ads.sponsored_category[category]} />
                  )}
                </h2>
                <span className="text-xs text-[#6B7280]">
                  {rest.length} {tFn(lang, "stories")}
                </span>
              </div>

              {loading ? (
                <div className="py-10 text-center text-[#6B7280]" data-testid="feed-loading">
                  {tFn(lang, "loading")}
                </div>
              ) : rest.length === 0 ? (
                <div className="py-10 text-center text-[#6B7280]" data-testid="feed-empty">
                  {tFn(lang, "no_articles")}
                </div>
              ) : (
                <div
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                  data-testid="article-grid"
                >
                  {rest.map((a, i) => (
                    <React.Fragment key={a.id}>
                      <ArticleCard
                        article={a}
                        index={i}
                        showIslandFlag={showIslandFlagOnCards}
                      />
                      {i === 5 && (
                        <div
                          className="sm:col-span-2"
                          data-testid="in-feed-cta-wrap"
                        >
                          <HeroCTAStrip
                            source="in_feed"
                            rounded
                            testid="in-feed-cta"
                          />
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <WeatherWidget weather={weather} island={island} />
              <MostReadWidget island={island} />
              <TrendingTopics data={trending} />
              <RectangleAd ad={ads.rectangle} />
              <WhatsAppCTA />
              <PushOptIn islandSlug={island} />
              <SourceTiles island={island} />
              <SidebarTallAd ad={ads.sidebar_tall} />
              <SidebarStickyAd ad={ads.sidebar_sticky} />
            </aside>
          </div>
        </main>

        <Footer onSubscribe={() => openSubscribe("footer")} />
      </div>
    </LangContext.Provider>
  );
}
